// One headline rule: its name, the number, and the plain sentence a trader
// will read on the card and in the drawer — the SAME sentence, written by
// `core/ruleSentences` as the author types.

import { For, Show, type JSX } from 'solid-js';

import NumberInput from './NumberInput';
import { useMoneyMark } from './builderWordsContext';

/** The unit of an amount of money, in the currency the template is priced
 *  in: shown by that currency's mark, never by a spelling of its own. */
export const MONEY = 'money';

interface HeadlineBlockProps {
  title: string;
  /** The sentence the drawer shows for this rule, or the plain words for "unset". */
  sentence: string;
  value: string;
  onInput: (value: string) => void;
  /** What the number is measured in: "%", `MONEY` or a noun such as "days". */
  unit: string;
  /** Present when the author may switch units. Switching CLEARS the number:
   *  5% and an amount of 5 are different rules, and no editor may convert one
   *  into the other on the author's behalf. */
  units?: { options: string[]; onPick: (unit: string) => void };
  min?: number;
  step?: number;
  /** The rule's own remaining terms, behind one disclosure. */
  children?: JSX.Element;
}

export default function HeadlineBlock(props: HeadlineBlockProps) {
  const mark = useMoneyMark();
  const money = () => props.unit === MONEY;
  return (
    <section class="ch-block">
      <div class="ch-block-text">
        <div class="ch-block-head">
          <h4 class="ch-block-title">{props.title}</h4>
          <Show when={props.units}>
            {(units) => (
              <div class="ch-units" role="group" aria-label={`${props.title} unit`}>
                <For each={units().options}>
                  {(option) => (
                    <button
                      type="button"
                      class="ch-unit"
                      aria-pressed={option === props.unit}
                      onClick={() => units().onPick(option)}
                    >
                      {option === MONEY ? mark : option}
                    </button>
                  )}
                </For>
              </div>
            )}
          </Show>
        </div>
        <p class="ch-block-sentence">{props.sentence}</p>
        <Show when={props.children}>
          <details class="ch-disclose">
            <summary class="ch-disclose-head">How this rule is measured</summary>
            <div class="ch-disclose-body">{props.children}</div>
          </details>
        </Show>
      </div>
      <div class="ch-block-value">
        <NumberInput
          value={props.value}
          onInput={props.onInput}
          prefix={money() ? mark : undefined}
          min={props.min ?? 0}
          step={props.step ?? 1}
          placeholder="none"
        />
        <Show when={!money()}>
          <span class="ch-meta">{props.unit}</span>
        </Show>
      </div>
    </section>
  );
}
